/**
 * Browser device-family classification.
 *
 * Viewport dimensions answer how much UI fits; they do not answer whether the
 * device has a desktop keyboard or a desktop performance envelope. Campfire
 * therefore classifies iOS/iPadOS and Android as mobile independently of the
 * current viewport, including iPadOS browsers that identify as macOS.
 *
 * Windows and ChromeOS stay desktop for now. Browsers expose neither a reliable
 * physical-keyboard signal nor a dependable convertible/tablet-mode signal, so
 * pointer media queries would misclassify ordinary touchscreen laptops.
 */

export interface MobileDeviceSignals {
  readonly userAgent: string;
  readonly platform: string;
  readonly maxTouchPoints: number;
  readonly userAgentDataMobile?: boolean;
}

export interface MobileDeviceClass {
  /** iOS/iPadOS, Android, or a browser explicitly emulating a mobile device. */
  readonly isMobile: boolean;
  /** Phone-family subset used only for the landscape compact-shell treatment. */
  readonly isPhone: boolean;
}

/** Pure classifier so browser signals can be checked without mutating globals. */
export function classifyMobileDevice(signals: MobileDeviceSignals): MobileDeviceClass {
  const { userAgent, platform, maxTouchPoints, userAgentDataMobile } = signals;
  const isIphoneOrIpod = /iPhone|iPod/i.test(userAgent);
  const isIpad = /iPad/i.test(userAgent);
  const isIpadOsDesktopMode =
    maxTouchPoints > 1 &&
    (/Macintosh|Mac OS X/i.test(userAgent) || /^Mac/i.test(platform));
  const isAndroid = /Android|OculusBrowser/i.test(userAgent);
  const isMobileHint = userAgentDataMobile === true;

  return {
    isMobile:
      isMobileHint || isIphoneOrIpod || isIpad || isIpadOsDesktopMode || isAndroid,
    isPhone:
      isIphoneOrIpod ||
      (isAndroid && /Mobile/i.test(userAgent)) ||
      (isMobileHint && !isIpad && !isIpadOsDesktopMode && !isAndroid),
  };
}

/** Snapshot the current browser's device-family signals. */
export function currentMobileDeviceClass(): MobileDeviceClass {
  if (typeof navigator === "undefined") return { isMobile: false, isPhone: false };

  const navigatorWithHints = navigator as Navigator & {
    readonly userAgentData?: { readonly mobile?: boolean };
  };
  return classifyMobileDevice({
    userAgent: navigator.userAgent ?? "",
    platform: navigator.platform ?? "",
    maxTouchPoints: navigator.maxTouchPoints ?? 0,
    userAgentDataMobile: navigatorWithHints.userAgentData?.mobile,
  });
}

export function isMobileDevice(): boolean {
  return currentMobileDeviceClass().isMobile;
}
